import type { TFunction } from 'i18next'
import { Tag, MessageCircle, CalendarDays, Bell, Link2, type LucideIcon } from 'lucide-react'
import { eventEditHref, eventHref, eventOfferHref } from '@/lib/event-links'
import type { Database } from '@/lib/types/database'

type NotificationRow = Database['public']['Tables']['notifications']['Row']
type NotificationType = Database['public']['Enums']['notification_type']

type NotificationMetadata = {
  event_id?: string
  offer_id?: string
  sub_event_id?: string
  announcement_id?: string
  rsvp_id?: string
  count?: number
  [key: string]: unknown
}

/**
 * The category → type mapping, exported as arrays because `useNotifications`
 * feeds them straight to PostgREST's `.in('type', …)`.
 *
 * It kept its own second copy of all three lists. That is how
 * `share_link_first_view` came to be missing from BOTH — there was no single
 * list to add it to, so «add the new type» was two edits and only zero got
 * done. One declaration now, two readers.
 */
export const NOTIFICATION_TYPES_BY_CATEGORY = {
  offers: ['offer_accepted', 'offer_viewed', 'offer_counter', 'offer_expiry'],
  events: ['rsvp', 'event_update', 'share_link_first_view'],
  chat: ['chat_message', 'announcement'],
} satisfies Record<string, NotificationType[]>

const OFFER_TYPES = new Set<NotificationType>(NOTIFICATION_TYPES_BY_CATEGORY.offers)

/**
 * `share_link_first_view` sits under `events` rather than in a category of its
 * own. It was in NO list, which had two invisible consequences: the filter
 * chips on /notifications each `.in('type', …)` against these, so the type
 * could only ever be seen under «Alle» — filter by anything and four real
 * notifications vanished — and `categorizeNotification` returned `'other'`, a
 * bucket nothing renders. Measured 10.08.2026: 4 rows in production, every one
 * addressed to the event owner.
 */
const EVENT_TYPES = new Set<NotificationType>(NOTIFICATION_TYPES_BY_CATEGORY.events)
const CHAT_TYPES = new Set<NotificationType>(NOTIFICATION_TYPES_BY_CATEGORY.chat)

function metadata(n: Pick<NotificationRow, 'metadata'>): NotificationMetadata {
  return (n.metadata ?? {}) as NotificationMetadata
}

export function getNotificationLink(n: Pick<NotificationRow, 'type' | 'metadata'>): string {
  const m = metadata(n)
  const eventId = m.event_id
  const offerId = m.offer_id

  switch (n.type) {
    // Tab deep-links go through `eventHref`, which owns the FIXED Norwegian
    // slugs — `?tab=` is the EDIT page's param and was silently ignored here,
    // landing every notification on Oversikt. The slugs used to be hand-written
    // in this file under a comment pointing at the copy they were copied from.
    case 'announcement':
      return eventId ? eventHref(eventId, 'announcements') : '/notifications'
    case 'chat_message':
      if (eventId && typeof m.offer_configuration_id === 'string')
        return eventOfferHref(eventId, 'responses')
      if (offerId) return eventHref(offerId, 'chat')
      return eventId ? eventHref(eventId, 'chat') : '/notifications'
    case 'rsvp':
    case 'event_update':
      return eventId ? eventHref(eventId) : '/notifications'
    case 'share_link_first_view':
      // Had no case at all, so it fell through `default` to '/notifications' —
      // clicking it from the notifications page navigated to the notifications
      // page. It goes to the editor's share tab, which is the surface that
      // holds view counts and the «send again» control, and which the
      // recipient can always reach: `get_share_data()` addresses this type to
      // `v_event_owner` and nobody else.
      return eventId ? eventEditHref(eventId, 'share') : '/notifications'
    case 'offer_accepted':
    case 'offer_counter':
      return offerId || eventId
        ? eventOfferHref(offerId || eventId!, 'responses')
        : '/notifications'
    case 'offer_viewed':
      return offerId || eventId ? eventOfferHref(offerId || eventId!, 'activity') : '/notifications'
    case 'offer_expiry':
      // offer_id IS the offer-event's id — offers render at /event/:id in
      // offer mode. A dedicated /offers/:id route does not exist.
      return offerId ? eventHref(offerId) : '/notifications'
    default:
      return '/notifications'
  }
}

export function getNotificationIcon(type: NotificationType): LucideIcon {
  if (OFFER_TYPES.has(type)) return Tag
  if (CHAT_TYPES.has(type)) return MessageCircle
  // Ahead of the EVENT_TYPES check it now sits inside: «a link was opened» is
  // wayfinding to the share tab, not to the event's calendar face.
  if (type === 'share_link_first_view') return Link2
  if (EVENT_TYPES.has(type)) return CalendarDays
  return Bell
}

/* B-1 + D-5 · `getNotificationColor` is gone, and it was broken as well as wrong.
   It returned a `var(--…)` STRING, and its one consumer interpolated it as if it
   were hex — `backgroundColor: \`${iconColor}1A\`` — producing
   `var(--color-primary-500)1A`, which every browser discards. So the 36px tinted
   medallion the design called for has never rendered once in production; what
   shipped was a bare glyph at full accent saturation on the row background.
   Verified by measuring the live page, not by reading the diff.
   It was wrong on top of broken: an offer notification is not a WARNING and a
   chat notification is not INFORMATIONAL — those are severity words spent on a
   category, which husregel 8 forbids, and two of the three hues were blues
   nobody could tell apart. The type is carried by the notification's own text.
   The glyph is now neutral, like every other leading icon. */

/**
 * «Martin Eide svarte NEI» is written by the database, in Norwegian, in capitals. The answer is in
 * `metadata.status`, so the sentence is rebuilt here in the reader's language and in the case the
 * rest of the app uses (sweep 24.10.2026). The stored title stays the fallback.
 */
function rsvpSentence(n: Pick<NotificationRow, 'type' | 'title' | 'metadata'>, t?: TFunction): string {
  const status = (n.metadata as { status?: string } | null)?.status
  if (n.type !== 'rsvp' || !t || !n.title || !status || !['yes', 'no', 'maybe'].includes(status)) return n.title
  const name = n.title.replace(/\s+(svarte (JA|NEI|KANSKJE)|oppdaterte påmelding)$/, '')
  // Literal keys: a template literal is invisible to check-i18n.
  return status === 'yes'
    ? t('notifications.rsvpSaid.yes', { name })
    : status === 'no'
      ? t('notifications.rsvpSaid.no', { name })
      : t('notifications.rsvpSaid.maybe', { name })
}

export function formatNotificationTitle(
  n: Pick<NotificationRow, 'type' | 'title' | 'metadata'>,
  t?: TFunction,
): string {
  const count = metadata(n).count
  if (!count || count <= 1) return rsvpSentence(n, t)
  // upsert_notification folds a burst of answers on one event into one row and keeps
  // only the LAST person's title, so «Henrik Prøve svarte JA (7)» named one person for
  // seven bookings and read as his party size (pass-1 re-score, 04.10.2026). The count
  // leads; the stored title survives as «sist».
  // The count is the title; «sist: …» goes to the body line (formatNotificationBody), because
  // at 390 px a two-line clamp cut «… svarte JA» off the title (pass-2 re-score, 04.10.2026).
  if (n.type === 'rsvp' && t) return t('notifications.rsvpBurst', { count })
  return `${n.title} (${count})`
}

/**
 * The body as it should read NOW. Stored text is frozen at write time, and one type froze a time
 * word into it: share_link_first_view says «har nettopp sett» on a row from last week, while the
 * row prints its own date beside it (03.10.2026 pass 1). The fact without the stale adverb.
 */
/**
 * W7 · a notification body is a one-line preview, not a document. Announcement bodies are
 * stored as markdown, so the preview printed «**Viktig:** se [programmet](https://…)».
 * This keeps the words and drops the syntax. Not a renderer and not a sanitizer: React
 * escapes the text, and nothing here is ever set as HTML.
 */
export function plainPreview(text: string): string {
  return text
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')          // images -> alt text
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')           // links -> label
    .replace(/(\*\*|__)(.+?)\1/g, '$2')                // bold
    .replace(/(^|[\s(])([*_])(\S(?:.*?\S)?)\2(?=[\s).,!?:;]|$)/g, '$1$3') // italic
    .replace(/`([^`]+)`/g, '$1')                       // inline code
    .replace(/^\s{0,3}(#{1,6}|>|[-*+]|\d+[.)])\s+/gm, '') // headings, quotes, list markers
    .replace(/\s+/g, ' ')
    .trim()
}

export function formatNotificationBody(
  n: Pick<NotificationRow, 'type' | 'body'> & Partial<Pick<NotificationRow, 'title' | 'metadata'>>,
  t?: TFunction,
): string | null {
  // A merged RSVP row: the event (the stored body) first, then who answered last. Event first
  // because a phone clamps the line, and «which event» is what the row is for; «Sist: …» first
  // lost the event in two of three rows at 390 px (pass-3 re-score, 04.10.2026).
  const count = n.metadata ? metadata(n as Pick<NotificationRow, 'metadata'>).count : undefined
  if (n.type === 'rsvp' && t && count && count > 1 && n.title) {
    return [n.body, t('notifications.rsvpLatest', { last: rsvpSentence({ type: n.type, title: n.title, metadata: n.metadata ?? null }, t) })].filter(Boolean).join(' · ')
  }
  if (!n.body) return null
  if (n.type === 'share_link_first_view') return n.body.replace(/\s+nettopp\b/i, '').replace(/\s+just\b/i, '')
  return plainPreview(n.body) || null
}

export function categorizeNotification(
  type: NotificationType,
): 'offers' | 'events' | 'chat' | 'other' {
  if (OFFER_TYPES.has(type)) return 'offers'
  if (CHAT_TYPES.has(type)) return 'chat'
  if (EVENT_TYPES.has(type)) return 'events'
  return 'other'
}
